<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const alert = useAlert;

const uiFlags = computed(() => store.getters['bulkCampaigns/getUIFlags']);
const bulkCampaigns = computed(() => store.getters['bulkCampaigns/getAll']);
const isLoading = computed(() => uiFlags.value.isFetching);

const filterStatus = ref('all');
const filterContact = ref('');

const todayISO = () => new Date().toISOString().slice(0, 10);
const periodType = ref('day');
const periodDate = ref(todayISO());
const currentPage = ref(1);
const totalCount = computed(() => store.getters['bulkCampaigns/getTotalCount']);
const hasMore = computed(() => bulkCampaigns.value.length < totalCount.value);
const periodMonth = computed({
  get: () => periodDate.value.slice(0, 7),
  set: val => {
    if (val) periodDate.value = `${val}-01`;
  },
});

const periodOptions = computed(() => [
  { value: 'day', label: t('BULK_CAMPAIGNS.PERIOD.DAY') },
  { value: 'week', label: t('BULK_CAMPAIGNS.PERIOD.WEEK') },
  { value: 'month', label: t('BULK_CAMPAIGNS.PERIOD.MONTH') },
]);

const fetchCampaigns = (page = 1) => {
  currentPage.value = page;
  return store.dispatch('bulkCampaigns/getAll', {
    periodType: periodType.value,
    date: periodDate.value,
    page,
  });
};

const loadMore = () => {
  if (!hasMore.value || isLoading.value) return;
  fetchCampaigns(currentPage.value + 1);
};

const filteredCampaigns = computed(() => {
  let campaigns = bulkCampaigns.value;
  if (!Array.isArray(campaigns)) return [];
  if (filterStatus.value !== 'all') {
    campaigns = campaigns.filter(c => c.status === filterStatus.value);
  }
  if (filterContact.value) {
    const q = filterContact.value.toLowerCase();
    campaigns = campaigns.filter(c => c.name?.toLowerCase().includes(q));
  }
  return campaigns;
});

const stats = computed(() => {
  const all = Array.isArray(bulkCampaigns.value) ? bulkCampaigns.value : [];
  return {
    total: all.length,
    scheduled: all.filter(c => c.status === 'scheduled').length,
    processing: all.filter(c => c.status === 'processing').length,
    completed: all.filter(c => c.status === 'completed').length,
    cancelled: all.filter(c => c.status === 'cancelled').length,
    paused: all.filter(c => c.status === 'paused').length,
    draft: all.filter(c => c.status === 'draft').length,
  };
});

const formatDate = dateStr => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusColor = status => {
  const map = {
    scheduled:
      'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    processing:
      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    completed:
      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    paused:
      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  };
  return map[status] || map.draft;
};

const statusLabel = status => {
  const map = {
    scheduled: t('BULK_CAMPAIGNS.STATUS.SCHEDULED'),
    processing: t('BULK_CAMPAIGNS.STATUS.PROCESSING'),
    completed: t('BULK_CAMPAIGNS.STATUS.COMPLETED'),
    cancelled: t('BULK_CAMPAIGNS.STATUS.CANCELLED'),
    failed: t('BULK_CAMPAIGNS.STATUS.FAILED'),
    paused: t('BULK_CAMPAIGNS.STATUS.PAUSED'),
    draft: t('BULK_CAMPAIGNS.STATUS.DRAFT'),
  };
  return map[status] || status;
};

const goToNew = () => router.push({ name: 'bulk_campaigns_new' });
const goToShow = id =>
  router.push({ name: 'bulk_campaigns_show', params: { id } });

const handleCancel = async campaign => {
  if (!window.confirm(t('BULK_CAMPAIGNS.CONFIRM_CANCEL'))) return;
  try {
    await store.dispatch('bulkCampaigns/cancel', { id: campaign.id });
    await fetchCampaigns();
    alert(t('BULK_CAMPAIGNS.CANCELLED_SUCCESS'));
  } catch (e) {
    alert(t('BULK_CAMPAIGNS.ERRORS.ACTION_FAILED'));
  }
};

const handlePause = async campaign => {
  try {
    await store.dispatch('bulkCampaigns/pause', { id: campaign.id });
    await fetchCampaigns();
    alert(t('BULK_CAMPAIGNS.PAUSED_SUCCESS'));
  } catch (e) {
    alert(t('BULK_CAMPAIGNS.ERRORS.ACTION_FAILED'));
  }
};

const handleResume = async campaign => {
  try {
    await store.dispatch('bulkCampaigns/resume', { id: campaign.id });
    await fetchCampaigns();
    alert(t('BULK_CAMPAIGNS.RESUMED_SUCCESS'));
  } catch (e) {
    alert(t('BULK_CAMPAIGNS.ERRORS.ACTION_FAILED'));
  }
};

const handleDelete = async campaign => {
  if (!window.confirm(t('BULK_CAMPAIGNS.CONFIRM_DELETE'))) return;
  try {
    await store.dispatch('bulkCampaigns/delete', { id: campaign.id });
    alert(t('BULK_CAMPAIGNS.DELETED_SUCCESS'));
  } catch (e) {
    alert(t('BULK_CAMPAIGNS.ERRORS.DELETE_FAILED'));
  }
};

const handleSendNow = async campaign => {
  try {
    await store.dispatch('bulkCampaigns/sendNow', { id: campaign.id });
    await fetchCampaigns();
    alert(t('BULK_CAMPAIGNS.SENT_NOW_SUCCESS'));
  } catch (e) {
    alert(t('BULK_CAMPAIGNS.ERRORS.ACTION_FAILED'));
  }
};

const statusOptions = computed(() => [
  {
    value: 'all',
    label: t('BULK_CAMPAIGNS.STATUS.ALL'),
    count: stats.value.total,
  },
  {
    value: 'scheduled',
    label: statusLabel('scheduled'),
    count: stats.value.scheduled,
  },
  {
    value: 'processing',
    label: statusLabel('processing'),
    count: stats.value.processing,
  },
  {
    value: 'completed',
    label: statusLabel('completed'),
    count: stats.value.completed,
  },
  {
    value: 'cancelled',
    label: statusLabel('cancelled'),
    count: stats.value.cancelled,
  },
  { value: 'paused', label: statusLabel('paused'), count: stats.value.paused },
]);

const canCancel = c =>
  ['scheduled', 'processing', 'paused', 'draft'].includes(c.status);
const canPause = c => c.status === 'processing';
const canResume = c => c.status === 'paused';
const canDelete = c =>
  ['completed', 'cancelled', 'failed', 'draft'].includes(c.status);
const canSendNow = c => ['draft', 'scheduled'].includes(c.status);

onMounted(() => {
  fetchCampaigns();
});

watch([periodType, periodDate], () => {
  fetchCampaigns();
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <span class="text-heading-1 text-n-slate-12">
            {{ t('BULK_CAMPAIGNS.TITLE') }}
          </span>
          <button
            class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-n-primary-300 dark:border-n-primary-700 text-n-slate-12 hover:opacity-80 transition-all bg-green-100 dark:bg-green-900/30"
            @click="goToNew"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
            {{ t('BULK_CAMPAIGNS.NEW') }}
          </button>
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-4">
        <div class="filters-row">
          <div class="filter-buttons">
            <button
              v-for="opt in periodOptions"
              :key="opt.value"
              class="px-3 py-1 text-sm rounded-md transition-all border"
              :class="
                periodType === opt.value
                  ? 'text-n-slate-12 shadow-sm font-medium border-n-primary-300 dark:border-n-primary-700 bg-green-100 dark:bg-green-900/30'
                  : 'text-n-text-light dark:text-n-text-muted hover:text-n-slate-12 border-transparent hover:border-n-divider dark:hover:border-n-divider bg-transparent'
              "
              @click="periodType = opt.value"
            >
              {{ opt.label }}
            </button>
          </div>
          <input
            v-if="periodType === 'month'"
            v-model="periodMonth"
            type="month"
            class="search-input period-input"
          />
          <input
            v-else
            v-model="periodDate"
            type="date"
            class="search-input period-input"
          />
          <button
            class="inline-flex items-center gap-1 px-3 py-1 text-sm rounded-md transition-all border border-n-primary-300 dark:border-n-primary-700 text-n-slate-12 hover:opacity-80 bg-green-100 dark:bg-green-900/30"
            @click="fetchCampaigns"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {{ t('BULK_CAMPAIGNS.REFRESH') }}
          </button>
        </div>
        <div class="filters-row">
          <div class="filter-buttons">
            <button
              v-for="opt in statusOptions"
              :key="opt.value"
              class="px-3 py-1 text-sm rounded-md transition-all border"
              :class="
                filterStatus === opt.value
                  ? 'text-n-slate-12 shadow-sm font-medium border-n-primary-300 dark:border-n-primary-700 bg-green-100 dark:bg-green-900/30'
                  : 'text-n-text-light dark:text-n-text-muted hover:text-n-slate-12 border-transparent hover:border-n-divider dark:hover:border-n-divider bg-transparent'
              "
              @click="filterStatus = opt.value"
            >
              {{ opt.label }}
              <span class="ml-1 text-xs opacity-60">{{ opt.count }}</span>
            </button>
          </div>
          <input
            v-model="filterContact"
            type="text"
            :placeholder="t('BULK_CAMPAIGNS.NAME_PLACEHOLDER')"
            class="search-input"
          />
        </div>

        <div v-if="isLoading" class="flex justify-center items-center py-10">
          <Spinner />
        </div>

        <div
          v-else-if="filteredCampaigns.length === 0"
          class="flex flex-col items-center justify-center py-16 text-center"
        >
          <div
            class="w-16 h-16 rounded-full bg-n-surface-2 dark:bg-n-surface-3 flex items-center justify-center mb-4"
          >
            <svg
              class="w-8 h-8 text-n-text-light dark:text-n-text-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <p class="text-sm text-n-text-light dark:text-n-text-muted">
            {{ t('BULK_CAMPAIGNS.EMPTY') }}
          </p>
        </div>

        <div v-else class="space-y-2">
          <div
            v-for="campaign in filteredCampaigns"
            :key="campaign.id"
            class="flex items-center gap-4 p-4 bg-white dark:bg-n-surface-2 rounded-xl border border-n-divider dark:border-n-divider hover:border-n-primary-300 dark:hover:border-n-primary-700 transition-colors cursor-pointer"
            @click="goToShow(campaign.id)"
          >
            <div
              class="flex-shrink-0 w-10 h-10 rounded-full bg-n-primary-100 dark:bg-n-primary-900/30 flex items-center justify-center"
            >
              <svg
                class="w-5 h-5 text-n-primary-600 dark:text-n-primary-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>

            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-sm font-medium text-n-slate-12 truncate">
                  {{ campaign.name }}
                </span>
                <span
                  class="px-2 py-0.5 text-xs rounded-full flex-shrink-0 bg-n-surface-3 text-n-text-light dark:text-n-text-muted font-mono"
                >
                  #{{ campaign.id }}
                </span>
                <span
                  class="px-2 py-0.5 text-xs rounded-full flex-shrink-0"
                  :class="statusColor(campaign.status)"
                >
                  {{ statusLabel(campaign.status) }}
                </span>
              </div>
              <div
                class="flex items-center gap-3 mt-1 text-xs text-n-text-light dark:text-n-text-muted"
              >
                <span>
                  {{ campaign.contacts_sent || 0 }}/{{
                    campaign.total_contacts || 0
                  }}
                  {{ t('BULK_CAMPAIGNS.DETAILS.CONTACTS_SENT') }}
                </span>
                <span v-if="campaign.total_batches > 0">
                  {{ t('BULK_CAMPAIGNS.DETAILS.CURRENT_BATCH') }}:
                  {{ campaign.current_batch }}/{{ campaign.total_batches }}
                </span>
                <span v-if="campaign.scheduled_at">{{
                  formatDate(campaign.scheduled_at)
                }}</span>
                <span v-if="campaign.inbox_name">{{
                  campaign.inbox_name
                }}</span>
              </div>
            </div>

            <div class="flex items-center gap-1 flex-shrink-0" @click.stop>
              <button
                v-if="canSendNow(campaign)"
                class="px-2 py-1 text-xs rounded-md text-n-primary-600 dark:text-n-primary-400 hover:bg-n-surface-3 dark:hover:bg-n-surface-3 transition-colors"
                :title="t('BULK_CAMPAIGNS.ACTIONS.SEND_NOW')"
                @click="handleSendNow(campaign)"
              >
                {{ t('BULK_CAMPAIGNS.ACTIONS.SEND_NOW') }}
              </button>
              <button
                v-if="canPause(campaign)"
                class="px-2 py-1 text-xs rounded-md text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                :title="t('BULK_CAMPAIGNS.ACTIONS.PAUSE')"
                @click="handlePause(campaign)"
              >
                {{ t('BULK_CAMPAIGNS.ACTIONS.PAUSE') }}
              </button>
              <button
                v-if="canResume(campaign)"
                class="px-2 py-1 text-xs rounded-md text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                :title="t('BULK_CAMPAIGNS.ACTIONS.RESUME')"
                @click="handleResume(campaign)"
              >
                {{ t('BULK_CAMPAIGNS.ACTIONS.RESUME') }}
              </button>
              <button
                v-if="canCancel(campaign)"
                class="px-2 py-1 text-xs rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                :title="t('BULK_CAMPAIGNS.ACTIONS.CANCEL')"
                @click="handleCancel(campaign)"
              >
                {{ t('BULK_CAMPAIGNS.ACTIONS.CANCEL') }}
              </button>
              <button
                v-if="canDelete(campaign)"
                class="p-1.5 rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                :title="t('BULK_CAMPAIGNS.ACTIONS.DELETE')"
                @click="handleDelete(campaign)"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="totalCount > 0"
          class="flex items-center justify-center gap-3 py-4"
        >
          <span class="text-xs text-n-text-muted">
            {{
              t('BULK_CAMPAIGNS.SHOWING', {
                count: filteredCampaigns.length,
                total: totalCount,
              })
            }}
          </span>
          <button
            v-if="hasMore"
            class="px-3 py-1.5 text-sm font-medium rounded-lg border border-n-primary-300 dark:border-n-primary-700 text-n-slate-12 hover:opacity-80"
            :disabled="isLoading"
            @click="loadMore"
          >
            {{ t('BULK_CAMPAIGNS.LOAD_MORE') }}
          </button>
        </div>
      </div>
    </main>
  </section>
</template>

<style scoped>
.filters-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.filters-row :deep(.filter-buttons) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  height: 42px;
  border: 1px solid var(--color-n-divider);
  border-radius: 8px;
  background-color: var(--color-n-surface-2);
}

.filters-row :deep(.search-input) {
  flex: 1;
  max-width: 360px;
  height: 24px;
  margin: 0 !important;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  background-color: white;
  color: var(--color-n-slate-12);
  outline: none;
}

.filters-row :deep(.search-input::placeholder) {
  color: var(--color-n-text-light);
}

.filters-row :deep(.search-input:focus) {
  outline: 2px solid var(--color-n-primary-300);
}

.filters-row :deep(.period-input) {
  flex: 0 0 auto;
  width: 170px;
}
</style>
